import { formatMinor as sharedFormatMinor } from '@zinqpay/money/src/index';

/**
 * Money on screen. Amounts arrive from the API as integer minor units (deni; 100 deni = 1 MKD) and
 * are never turned into floats: formatting goes through the shared BigInt formatter of
 * @zinqpay/money ('.' thousands, ',' decimals) plus „ ден.“ — the same in Macedonian and English
 * (docs/i18n/glossary-mk.md, "Amount on a screen").
 */
export type MinorInput = number | bigint | string;

function toBigInt(amount: MinorInput): bigint {
  if (typeof amount === 'bigint') return amount;
  if (typeof amount === 'number') {
    if (!Number.isSafeInteger(amount)) throw new RangeError(`Not an integer minor amount: ${amount}`);
    return BigInt(amount);
  }
  if (!/^-?\d+$/.test(amount)) throw new RangeError(`Not an integer minor amount: ${amount}`);
  return BigInt(amount);
}

/** 123450 -> '1.234,50' */
export function formatMinor(amount: MinorInput): string {
  return sharedFormatMinor(toBigInt(amount));
}

/** 123450 -> '1.234,50 ден.' */
export function formatDen(amount: MinorInput): string {
  return `${formatMinor(amount)} ден.`;
}

/** Signed: received money gets a plus sign. 100000 -> '+1.000,00 ден.' */
export function formatDenSigned(amount: MinorInput): string {
  const n = toBigInt(amount);
  return n > 0n ? `+${formatDen(n)}` : formatDen(n);
}

/**
 * A whole price may drop „,00“ (glossary): 35000 -> '350 ден.', 9950 -> '99,50 ден.'.
 */
export function formatPrice(amount: MinorInput): string {
  const n = toBigInt(amount);
  if (n % 100n === 0n) {
    const whole = sharedFormatMinor(n).replace(/,00$/, '');
    return `${whole} ден.`;
  }
  return formatDen(n);
}

/**
 * A share 0..1 as a percentage with one decimal and the locale's decimal mark:
 * 0.625 -> '62,5%' (mk) / '62.5%' (en). Display only (never money).
 */
export function formatPercent(share: number, locale: 'mk' | 'en'): string {
  const tenths = Math.round(share * 1000);
  const whole = Math.trunc(tenths / 10);
  const frac = Math.abs(tenths % 10);
  const text = frac === 0 ? `${whole}` : `${whole}${locale === 'mk' ? ',' : '.'}${frac}`;
  return `${text}%`;
}

/** A display number with one decimal: 72.5 -> '72,5' (mk) / '72.5' (en); integers stay bare. */
export function formatDecimal(value: number, locale: 'mk' | 'en'): string {
  const tenths = Math.round(value * 10);
  const whole = Math.trunc(tenths / 10);
  const frac = Math.abs(tenths % 10);
  const sign = tenths < 0 && whole === 0 ? '-' : '';
  return frac === 0 ? `${sign}${whole}` : `${sign}${whole}${locale === 'mk' ? ',' : '.'}${frac}`;
}
